import { useState } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { useGetBondsByUserIdQuery, useDeleteBondMutation } from 'redux/features/employeeBondApi';
import { calculateBondDuration } from 'utils/bondUtils';
import { formatDate } from 'utils/timeTracker';
import Icon from '../../components/AppIcon';
import AddBondModal from '../employee-bonds/components/AddBondModal';

const BondStatusBadge = ({ status }) => {
  const colors = {
    ACTIVE: 'bg-green-100 text-green-700 border border-green-200',
    EXPIRED: 'bg-red-100 text-red-700 border border-red-200',
    TERMINATED: 'bg-gray-100 text-gray-700 border border-gray-200',
    PENDING: 'bg-yellow-100 text-yellow-700 border border-yellow-200',
  };

  return (
    <span
      className={`px-2 py-1 rounded-md text-xs font-bold uppercase tracking-wider ${colors[status] || colors.PENDING}`}
    >
      {status}
    </span>
  );
};

const BondDetailsTab = ({ user }) => {
  const userId = user?.id;
  const {
    data: bondsData,
    isLoading,
    refetch,
  } = useGetBondsByUserIdQuery(userId, { skip: !userId });
  const [deleteBond, { isLoading: isDeleting }] = useDeleteBondMutation();
  const [deletingBondId, setDeletingBondId] = useState(null);

  const [showBondModal, setShowBondModal] = useState(false);
  const [modalMode, setModalMode] = useState(null);
  const [selectedBond, setSelectedBond] = useState(null);

  const bonds = bondsData || [];

  const handleAddBond = () => {
    setSelectedBond(null);
    setModalMode('add');
    setShowBondModal(true);
  };

  const handleRenewBond = () => {
    setSelectedBond(null);
    setModalMode('renew');
    setShowBondModal(true);
  };

  const handleEditBond = (bond) => {
    setSelectedBond(bond);
    setModalMode('edit');
    setShowBondModal(true);
  };

  const handleCloseModal = () => {
    setShowBondModal(false);
    setSelectedBond(null);
    setModalMode(null);
  };

  const handleBondCreated = async () => {
    handleCloseModal();
  };

  const handleDeleteBond = async (bondId) => {
    const result = await Swal.fire({
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete',
    });

    if (!result.isConfirmed) return;

    setDeletingBondId(bondId);
    try {
      await deleteBond(bondId).unwrap();
      toast.success('Bond deleted successfully');
      refetch();
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to delete bond');
    } finally {
      setDeletingBondId(null);
    }
  };

  const employee = user
    ? {
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        employee_id: user.employee_id,
      }
    : null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Icon name="FileText" size={20} color="var(--color-primary)" />
          <h2 className="text-lg font-medium text-gray-900">Bond Details</h2>
        </div>
        <div className="flex items-center space-x-3">
          {bonds.length > 0 ? (
            <button
              type="button"
              onClick={handleRenewBond}
              className="inline-flex items-center px-3 py-2 border border-primary text-sm leading-4 font-medium rounded-md text-primary bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              <Icon name="RefreshCw" size={16} className="mr-1.5" />
              Renew Bond
            </button>
          ) : (
            <button
              type="button"
              onClick={handleAddBond}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              <Icon name="Plus" size={16} className="mr-1.5" />
              Add Bond
            </button>
          )}
        </div>
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : bonds.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <Icon
              name="FileText"
              size={48}
              color="var(--color-gray-300)"
              className="mx-auto mb-4"
            />
            <p className="text-lg font-medium text-gray-500 mb-2">No bonds found</p>
            <p className="text-sm text-gray-400 mb-6">This employee has no bonds yet.</p>
            <button
              type="button"
              onClick={handleAddBond}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-600 transition-colors"
            >
              <Icon name="Plus" size={16} className="mr-1.5" />
              Add First Bond
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Start Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    End Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Signed Date
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {[...bonds]
                  .sort((a, b) => new Date(b.start_date) - new Date(a.start_date))
                  .map((bond, index) => (
                    <tr key={bond.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-mono">
                        {index + 1}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(bond.start_date)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {bond.end_date ? (
                          formatDate(bond.end_date)
                        ) : (
                          <span className="text-gray-400 italic">Ongoing</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {calculateBondDuration(bond.start_date, bond.end_date)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <BondStatusBadge status={bond.status} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {bond.signed_date ? (
                          formatDate(bond.signed_date)
                        ) : (
                          <span className="text-gray-400">--</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            type="button"
                            onClick={() => handleEditBond(bond)}
                            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                            title="Edit Bond"
                          >
                            <Icon name="Edit3" size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteBond(bond.id)}
                            disabled={isDeleting && deletingBondId === bond.id}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Delete Bond"
                          >
                            {isDeleting && deletingBondId === bond.id ? (
                              <Icon name="Loader" size={14} className="animate-spin" />
                            ) : (
                              <Icon name="Trash2" size={14} />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AddBondModal
        isOpen={showBondModal}
        onClose={handleCloseModal}
        employee={employee}
        bond={selectedBond}
        mode={modalMode}
        onBondCreated={handleBondCreated}
      />
    </div>
  );
};

export default BondDetailsTab;
